// src/pages/PurgedFaxes.jsx
// Faxes deleted from the Outgoing / Incoming lists. They stay in the logs,
// reports and billing; this tab shows them to roles allowed to see deleted
// faxes. Admins and Super Admins can restore one to the lists; nobody else.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import EmptyState from '../components/EmptyState';
import { fetchPurged, restoreFax, getDownloadUrl, downloadSheet } from '../utils/api';
import SortTh, { useSort } from '../components/SortTh';
import { timeZone, tzLabel } from '../utils/timeZone';
import TeamFilter, { useClientTeams, teamText } from '../components/TeamFilter';
import { PAGE_TITLE } from '../theme/tabs';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import { useContactNames } from '../hooks/useContactNames';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { useClientWord } from '../hooks/useClientWord';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ' + tzLabel(new Date(iso));
}
function formatFax(num) {
  const n = String(num || '').replace(/\D/g, '');
  if (n.length === 11) return `+${n[0]} (${n.slice(1, 4)}) ${n.slice(4, 7)}-${n.slice(7)}`;
  if (n.length === 10) return `+1 (${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
  return num || '—';
}

export default function PurgedFaxes() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, can } = useAuth();
  const { notify, confirm } = useNotify();
  const [rows, setRows]       = useState([]);
  const [canRestore, setCanRestore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [kind, setKind]       = useState('all');
  const clientTeams = useClientTeams();                   // Team filter (3.19.5)
  const [teamPick, setTeamPick] = useState('');
  const [busy, setBusy]       = useState('');
  const [show, setShow]       = useState('50');   // rows per page (3.26.0)

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetchPurged(getToken);
      setRows(r.data || []);
      setCanRestore(!!r.canRestore);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => rows.filter(r => (kind === 'all' || r.type === kind) && (!teamPick || (r.teamNames || []).includes(teamPick))), [rows, kind, teamPick]);
  const contactName = useContactNames(useMemo(() => filteredRows.map(r => ({ accountId: r.accountId, number: r.type === 'outgoing' ? r.toNumber : r.fromNumber })), [filteredRows]), ['number']);   // Fax Contacts names when hovering a number (3.25.0)
  const showTeam = clientTeams.length > 0 || rows.some(r => (r.teamNames || []).length);
  // Sorting and Excel (3.16.0)
  const { sorted, sort, onSort } = useSort(filteredRows, {
    type: r => r.type, team: teamText, date: r => r.at || '', from: r => (r.type === 'outgoing' ? r.fromEmail : String(r.fromNumber || '').replace(/\D/g, '')),
    to: r => String(r.toNumber || '').replace(/\D/g, ''), file: r => r.fileName, pages: r => Number(r.pages) || 0, deleted: r => r.deletedAt || '',
  });
  const { pageRows: shown, pager } = usePager(sorted, show);
  async function downloadXlsx() {
    try {
      await downloadSheet('Trash', [
        { header: 'From', get: r => (r.type === 'outgoing' ? 'Outbox' : 'Inbox'), width: 10 },
        { header: clientWord, get: r => r.accountName || '', width: 16 },
        ...(showTeam ? [{ header: 'Team', get: teamText, width: 18 }] : []),
        { header: 'Date', get: r => formatDate(r.at), width: 24 },
        { header: 'From', get: r => (r.type === 'outgoing' ? (r.fromEmail || '') : formatFax(r.fromNumber)), width: 28 },
        { header: 'To', get: r => formatFax(r.toNumber), width: 18 },
        { header: 'File', get: r => r.fileName || '', width: 30 },
        { header: 'Pages', get: r => r.pages ?? '', width: 7 },
        { header: 'Deleted', get: r => formatDate(r.deletedAt), width: 24 },
        { header: 'Deleted by', get: r => r.deletedBy || '', width: 28 },
      ], sorted, getToken);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
  }

  async function download(r) {
    setBusy(`dl:${r.faxId}`);
    try {
      const { url, fileName } = await getDownloadUrl(r.type, r.faxId, getToken);
      const a = document.createElement('a'); a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
    setBusy('');
  }

  async function restore(r) {
    const ok = await confirm({
      title: 'Restore this fax?',
      message: `It goes back into the ${r.type === 'outgoing' ? 'Outbox' : 'Inbox'} for everyone who can see it.${r.type === 'outgoing' ? ' All its attempts are restored together.' : ''}`,
      confirmLabel: 'Restore',
    });
    if (!ok) return;
    setBusy(`rs:${r.faxId}`);
    try {
      await restoreFax(r.type, r.faxId, getToken);
      notify({ bell: 'fax', tone: 'success', title: 'Fax restored' });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't restore the fax", detail: e.message }); }
    setBusy('');
  }

  return (
    <div>
      <div style={S.top}>
        <div>
          <h2 style={S.title} className="cp-title"><Emo e="🗑️" /> Trash</h2>
          <p style={S.sub}>Faxes deleted from Outbox and Inbox. They're still counted in the logs, reports and Usage.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {can('fax.reports') && <button onClick={downloadXlsx} disabled={!shown.length} style={{ ...S.refreshBtn, opacity: shown.length ? 1 : 0.5 }} title="Download what's shown as an Excel file"><Emo e="⬇" /> Download (.xlsx)</button>}
          <button onClick={load} style={S.refreshBtn}><Emo e="↻" /> Refresh</button>
        </div>
      </div>
      <div style={S.filters} role="tablist" aria-label="Show">
        {[['all', 'All'], ['outgoing', 'Outbox'], ['incoming', 'Inbox']].map(([id, label]) => (
          <button key={id} role="tab" aria-selected={kind === id} onClick={() => setKind(id)}
            style={{ ...S.chip, ...(kind === id ? S.chipOn : {}) }}>
            {label} <strong>{rows.filter(r => id === 'all' || r.type === id).length}</strong>
          </button>
        ))}
        <TeamFilter teams={clientTeams} value={teamPick} onChange={setTeamPick} byName
          groupStyle={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }} labelStyle={{ fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 }}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' }} />
        <ShowSelect value={show} onChange={setShow} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
          labelStyle={{ fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 }} style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' }} />
      </div>
      {error && <div role="alert" style={S.error}>{error}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <SortTh k="type" label="Type" sort={sort} onSort={onSort} style={S.th} />{showTeam && <SortTh k="team" label="Team" sort={sort} onSort={onSort} style={S.th} />}<SortTh k="date" label="Date" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="from" label="From" sort={sort} onSort={onSort} style={S.th} /><SortTh k="to" label="To" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="file" label="File" sort={sort} onSort={onSort} style={S.th} /><SortTh k="pages" label="Pages" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="deleted" label="Deleted" sort={sort} onSort={onSort} style={S.th} /><th style={S.th}><span style={S.srOnly}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={9} style={S.empty}><Loader label="Loading deleted faxes…" /></td></tr>
              : !shown.length ? <tr><td colSpan={9} style={S.empty}>
                <EmptyState filtered={kind !== 'all' || !!teamPick} onClear={() => { setKind('all'); setTeamPick(''); }} clearLabel="Show all"
                  filteredText={`No deleted faxes${kind === 'all' ? '' : ` from ${kind === 'outgoing' ? 'Outbox' : 'Inbox'}`}${teamPick ? ` for ${teamPick}` : ''}.`}
                  emptyText="No deleted faxes. To delete one, tick it in Outbox or Inbox and click Delete Selected, or open it and click Delete." />
              </td></tr>
              : shown.map((r, i) => (
                <tr key={`${r.type}:${r.faxId}`} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                  <td style={S.td}><span style={{ ...S.badge, ...(r.type === 'outgoing' ? S.badgeOut : S.badgeIn) }}>{r.type === 'outgoing' ? 'Outbox' : 'Inbox'}</span>
                    {r.accountName && <div style={{ fontSize: 11, color: 'var(--t-78909c)', marginTop: 3 }}>{r.accountName}</div>}</td>
                  {showTeam && <td style={S.td}>{teamText(r) || '—'}</td>}
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(r.at)}</td>
                  <td style={S.td}>{r.type === 'outgoing' ? (r.fromEmail || '—') : <span title={contactName(r.accountId, r.fromNumber) || undefined}>{formatFax(r.fromNumber)}</span>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}><span title={(r.type === 'outgoing' ? contactName(r.accountId, r.toNumber) : 'Your fax line') || undefined}>{formatFax(r.toNumber)}</span></td>
                  <td style={{ ...S.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fileName || ''}>{r.fileName || '—'}</td>
                  <td style={S.td}>{r.pages ?? '—'}</td>
                  <td style={{ ...S.td, fontSize: 12 }}>{formatDate(r.deletedAt)}<div style={S.meta}>by {r.deletedBy || '—'}</div></td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r.hasFile && can('fax.files') && (
                      <button onClick={() => download(r)} disabled={!!busy} style={S.actionBtn} title="Download the fax file"><Emo e="⬇" /></button>
                    )}
                    {canRestore && (
                      <button onClick={() => restore(r)} disabled={!!busy} style={{ ...S.actionBtn, marginLeft: 6 }}>Restore</button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {!loading && <Pager {...pager} label="deleted faxes" />}
    </div>
  );
}

const S = {
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  title: { ...PAGE_TITLE, margin: '0 0 4px' },
  sub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, lineHeight: 1.5, maxWidth: 720 },
  refreshBtn: { padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  filters: { display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' },
  chip: { padding: '6px 12px', borderRadius: 16, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  chipOn: { background: 'var(--c-e0f2f1)', border: '1.5px solid var(--c-00897b)', color: 'var(--t-00695c)' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, marginBottom: 10, fontSize: 13 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-546e7a)' },
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
  meta: { fontSize: 11, color: 'var(--t-90a4ae)', marginTop: 2 },
  badge: { padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
  badgeOut: { background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)' },
  badgeIn: { background: 'var(--c-e3f2fd)', color: 'var(--t-0d47a1)' },
  actionBtn: { padding: '6px 12px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  empty: { textAlign: 'center', padding: '36px 0', color: 'var(--t-90a4ae)', fontSize: 14 },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
};
